import { css } from '@emotion/css';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../features';
import { actions } from '../../../features/dream';
import { ConfirmButton } from '../../shared/buttons/ConfirmButton';
import { NormalButton } from '../../shared/buttons/NormalButton';
import { ErrorText } from './ErrorText';

interface ConfirmDeleteProps {
  title: string;
  description: string;
  confirm: string;
  onCancelClick: (address: string) => void;
}

export const ConfirmDelete: React.FC<ConfirmDeleteProps> = ({
  title,
  description,
  confirm,
  onCancelClick
}) => {
  const { t } = useTranslation('translation');
  const history = useHistory();
  const params = useParams<{ address?: string }>();
  const { deleteState } = useSelector((state: RootState) => state.dream.historyDetail);
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(actions.resetDeleteLandHistory());
    };
  }, [dispatch]);

  useEffect(() => {
    if (deleteState?.state === 'ok') {
      history.replace('/players/dream/history/delete-succeeded');
    }
  }, [deleteState, history, dispatch]);

  const handleConfirmClick = () => {
    if (params?.address) {
      const { address } = params;
      dispatch(actions.deleteLandHistory({ address }));
    }
  };

  const handleCancelClick = () => {
    if (params?.address) {
      const { address } = params;
      onCancelClick(address);
    }
  };

  const isLoading = deleteState?.state === 'pending';

  return (
    <section
      className={css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        height: 100%;
        .title {
          font-size: 20px;
          text-align: center;
          color: #4d3906;
          animation-delay: 0.1s;
        }
        .desc {
          flex: 1 1 auto;
          padding: 12px 0 20px;
          animation-delay: 0.2s;
        }
        .error {
          color: #e60012;
          font-size: 12px;
          text-align: center;
          margin: 16px auto;
        }
        .button {
          width: 100%;
          margin: 8px 0 0;
        }
        @media (max-width: 369px) {
          .desc {
            font-size: 14px;
          }
        }
      `}
    >
      <h1 className="title">{title}</h1>
      <p className="desc">{description}</p>
      {deleteState?.state === 'error' && <ErrorText errorType={deleteState.error} />}
      <div className="button">
        <ConfirmButton background={'#E60012'} onClick={handleConfirmClick} loading={isLoading}>
          {confirm}
        </ConfirmButton>
      </div>
      <div className="button">
        <NormalButton onClick={handleCancelClick} disabled={isLoading}>
          {t('dialog_button_cancel')}
        </NormalButton>
      </div>
    </section>
  );
};
